import {
  blob,
  dropCached,
  dropKey,
  isCached,
  hash,
  linear,
  oval,
  radial,
  raster,
  rgba,
  shade,
  TAU,
} from "./art/kit";

type Ctx = CanvasRenderingContext2D;
/** Terrain scrolls with ground units (engine: `startY + travel * 0.72`). */
export const GROUND_SPEED = 0.72;
/** Short tiles keep each first-time raster small enough to avoid frame hitches. */
const TILE = 256;

/** Player-position parallax used by the loose overlay planes (clouds, mist, snow). */
export function sceneryOffset(tick: number, depth: number, x: number, y: number) {
  return {
    x: (360 - x) * depth * 0.035,
    y: (tick * (0.12 + depth * 0.28) + (1000 - y) * depth * 0.025) % 1600,
  };
}

interface Layer {
  id: string;
  /** Vertical scroll speed per tick. */
  speed: number;
  /** Horizontal camera factor: 1 = glued to world coordinates. */
  parallax: number;
  /** Raster resolution; soft overlays are cached at half resolution. */
  res: number;
  alpha: number;
  add?: boolean;
  /** Overlay depth for the player-position parallax (0 = none). */
  depth: number;
  paint(c: Ctx, k: number, width: number): void;
}

/* ---------------------------------------------------------------- helpers */

/**
 * Iterate hashed grid cells overlapping tile k (plus a margin), in global
 * coordinates. Objects spanning a tile seam are drawn by both tiles, so the
 * terrain is seamless without storing anything.
 */
function scatter(
  k: number,
  width: number,
  cell: number,
  margin: number,
  salt: number,
  fn: (x: number, y: number, h: (n: number) => number, cx: number, cy: number) => void,
) {
  const g0 = k * TILE,
    from = Math.floor((g0 - margin) / cell),
    to = Math.floor((g0 + TILE + margin) / cell),
    cols = Math.ceil(width / cell) + 1;
  for (let cy = from; cy <= to; cy++)
    for (let cx = -1; cx < cols; cx++) {
      const h = (n: number) => hash(salt, cx, cy, n);
      fn(cx * cell + h(1) * cell, cy * cell + h(2) * cell - g0, h, cx, cy);
    }
}
/** Continuous meander in global coordinates (rivers, leads, roads). */
function meander(G: number, seed: number, amplitude = 900) {
  return (
    720 +
    amplitude * Math.sin(G * 0.00042 + seed) +
    170 * Math.sin(G * 0.0017 + seed * 3.1) +
    40 * Math.sin(G * 0.0061 + seed * 7.7)
  );
}
function ribbon(
  c: Ctx,
  k: number,
  center: (G: number) => number,
  half: (G: number) => number,
  fill: string | CanvasGradient,
) {
  const g0 = k * TILE,
    left: number[][] = [],
    right: number[][] = [];
  for (let y = -60; y <= TILE + 60; y += 12) {
    const G = g0 + y,
      x = center(G),
      w = half(G);
    left.push([x - w, y]);
    right.push([x + w, y]);
  }
  c.beginPath();
  [...left, ...right.reverse()].forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
  c.closePath();
  c.fillStyle = fill;
  c.fill();
}
function riverLines(c: Ctx, k: number, center: (G: number) => number, half: (G: number) => number, color: string) {
  const g0 = k * TILE;
  c.strokeStyle = color;
  c.lineWidth = 2;
  c.lineCap = "round";
  for (let y = -40; y <= TILE + 40; y += 22) {
    const G = g0 + y,
      h = hash(91, Math.floor(G / 22));
    if (h < 0.35) continue;
    const x = center(G) + (h - 0.5) * half(G) * 1.2,
      len = 10 + h * 22;
    c.beginPath();
    c.moveTo(x - len / 2, y);
    c.lineTo(x + len / 2, y + 1);
    c.stroke();
  }
}
function treeCrown(c: Ctx, x: number, y: number, r: number, base: string, seed: number, shadow = 0.32) {
  oval(c, x + r * 0.38, y + r * 0.42, r * 1.02, r * 0.9, `rgba(12,28,24,${shadow})`);
  blob(c, x, y, r, seed, 6, 0.16);
  c.fillStyle = shade(base, -0.28);
  c.fill();
  blob(c, x - r * 0.1, y - r * 0.12, r * 0.8, seed + 0.3, 5, 0.18);
  c.fillStyle = base;
  c.fill();
  blob(c, x - r * 0.3, y - r * 0.32, r * 0.42, seed + 0.6, 4, 0.2);
  c.fillStyle = shade(base, 0.2);
  c.fill();
}
function pine(c: Ctx, x: number, y: number, r: number, color: string, snow: string | null) {
  oval(c, x + r * 0.5, y + r * 0.55, r * 0.95, r * 0.75, "rgba(20,40,60,0.26)");
  for (const [scale, tone] of [
    [1, -0.18],
    [0.72, 0.02],
    [0.44, 0.16],
  ] as const) {
    c.beginPath();
    for (let i = 0; i < 18; i++) {
      const a = (i / 18) * TAU + scale,
        rr = r * scale * (i % 2 ? 0.78 : 1);
      i ? c.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr) : c.moveTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
    }
    c.closePath();
    c.fillStyle = shade(color, tone);
    c.fill();
  }
  if (snow) {
    blob(c, x - r * 0.2, y - r * 0.2, r * 0.5, x, 4, 0.25);
    c.fillStyle = snow;
    c.fill();
  }
}
function haze(c: Ctx, width: number, color: string) {
  c.fillStyle = color;
  c.fillRect(0, 0, width, TILE);
}

/* -------------------------------------------------------- 0 晨光云海 */

const FIELD_COLORS = ["#B9D18E", "#CBD98C", "#A5C47F", "#DCCB86", "#BCCB8F", "#93B77C", "#E4D29A", "#C4D7A0"];
function farmland(c: Ctx, k: number, width: number) {
  const g0 = k * TILE;
  c.fillStyle = "#A7C487";
  c.fillRect(0, 0, width, TILE);
  // Patchwork of fields on a slightly rotated global grid.
  const a = -0.2,
    cos = Math.cos(a),
    sin = Math.sin(a);
  const corners = [
    [0, g0 - 80],
    [width, g0 - 80],
    [0, g0 + TILE + 80],
    [width, g0 + TILE + 80],
  ].map(([x, y]) => [x * cos + y * sin, -x * sin + y * cos]);
  const u0 = Math.min(...corners.map((p) => p[0])),
    u1 = Math.max(...corners.map((p) => p[0])),
    v0 = Math.min(...corners.map((p) => p[1])),
    v1 = Math.max(...corners.map((p) => p[1]));
  c.save();
  c.translate(0, -g0);
  c.rotate(a);
  const rowH = 130;
  for (let j = Math.floor(v0 / rowH); j <= Math.floor(v1 / rowH); j++) {
    const offset = hash(7, j) * 190;
    for (let i = Math.floor((u0 - offset) / 190) - 1; i <= Math.floor((u1 - offset) / 190) + 1; i++) {
      const h = (n: number) => hash(11, i, j, n),
        x = i * 190 + offset,
        y = j * rowH,
        w = 190,
        color = FIELD_COLORS[Math.floor(h(1) * FIELD_COLORS.length)];
      c.fillStyle = color;
      c.fillRect(x + 3, y + 3, w - 6, rowH - 6);
      c.strokeStyle = shade(color, -0.1);
      c.lineWidth = 2;
      c.beginPath();
      if (h(2) < 0.5)
        for (let px = x + 12; px < x + w - 6; px += 11) {
          c.moveTo(px, y + 7);
          c.lineTo(px, y + rowH - 7);
        }
      else
        for (let py = y + 12; py < y + rowH - 6; py += 11) {
          c.moveTo(x + 7, py);
          c.lineTo(x + w - 7, py);
        }
      c.stroke();
      // Hedgerows with little trees on some borders.
      if (h(3) < 0.55)
        for (let px = x + 8; px < x + w; px += 17) {
          oval(c, px + 3, y + 4, 8, 6, "rgba(30,60,30,0.25)");
          c.beginPath();
          c.arc(px, y + 1, 7 + h(px) * 2, 0, TAU);
          c.fillStyle = "#5F8F52";
          c.fill();
          c.beginPath();
          c.arc(px - 2, y - 1, 3.5, 0, TAU);
          c.fillStyle = "#86B06A";
          c.fill();
        }
    }
  }
  c.restore();
  // Country road and river are continuous in global coordinates.
  const road = (G: number) => meander(G, 4.1, 620);
  ribbon(c, k, road, () => 17, "#C9B58B");
  ribbon(c, k, road, () => 12, "#E6D6AE");
  const river = (G: number) => meander(G, 1.3),
    half = (G: number) => 34 + 12 * Math.sin(G * 0.003);
  ribbon(c, k, river, (G) => half(G) + 14, "#D9CC9E");
  ribbon(c, k, river, (G) => half(G) + 5, "#8FB9B4");
  ribbon(c, k, river, half, linear(c, 0, 0, width, 0, [[0, "#5E9EB6"], [1, "#6FB1C1"]]));
  riverLines(c, k, river, half, "rgba(235,250,255,0.55)");
  // Farm houses and small copses.
  scatter(k, width, 230, 60, 21, (x, y, h) => {
    if (Math.abs(x - river(y + g0)) < 90) return;
    if (h(3) < 0.3) {
      const roof = ["#C8645A", "#5E8FA8", "#D49A4E", "#8A6BA8"][Math.floor(h(4) * 4)],
        w = 26 + h(5) * 16,
        d = 18 + h(6) * 8;
      c.fillStyle = "rgba(30,40,30,0.3)";
      c.fillRect(x + 5, y + 6, w, d);
      c.fillStyle = roof;
      c.fillRect(x, y, w, d / 2);
      c.fillStyle = shade(roof, -0.25);
      c.fillRect(x, y + d / 2, w, d / 2);
      c.fillStyle = "#EFE4CC";
      c.fillRect(x, y + d, w, 5);
    } else if (h(3) < 0.75)
      for (let i = 0; i < 3 + h(7) * 5; i++)
        treeCrown(c, x + (hash(i, x) - 0.5) * 60, y + (hash(y, i) - 0.5) * 50, 11 + hash(i, y, 3) * 8, "#5C9152", h(i + 9), 0.28);
  });
  haze(c, width, "rgba(236,244,246,0.26)");
}
function cloudBank(c: Ctx, x: number, y: number, size: number, seed: number, alpha: number) {
  const puffs = 6 + Math.floor(hash(seed, 1) * 5),
    list: [number, number, number][] = [];
  for (let i = 0; i < puffs; i++) {
    const a = hash(seed, i, 2) * TAU,
      d = hash(seed, i, 3) * size * 0.9;
    list.push([x + Math.cos(a) * d * 1.5, y + Math.sin(a) * d * 0.6, size * (0.45 + hash(seed, i, 4) * 0.45)]);
  }
  for (const [px, py, r] of list) oval(c, px + r * 0.15, py + r * 0.35, r, r * 0.8, rgba("#8FA3C4", alpha * 0.55));
  for (const [px, py, r] of list) oval(c, px, py, r, r * 0.8, rgba("#DCE5F2", alpha));
  for (const [px, py, r] of list) oval(c, px - r * 0.18, py - r * 0.22, r * 0.72, r * 0.52, rgba("#FFFFFF", alpha));
}
/* -------------------------------------------------------- 1 彩绘都市 */

const ROOFS = ["#E3A28C", "#8EC1BC", "#E6C47E", "#B2A3D2", "#EDE0C6", "#98B5D6", "#D69292", "#A5C68F", "#F0B97A"];
const AVENUE = 360,
  STREET = 300;
function building(c: Ctx, x: number, y: number, w: number, d: number, seed: number) {
  const h = (n: number) => hash(seed, n),
    roof = ROOFS[Math.floor(h(1) * ROOFS.length)],
    lift = 8 + h(2) * 26;
  // Cast shadow falls to the lower right.
  c.fillStyle = "rgba(24,28,44,0.3)";
  c.beginPath();
  c.moveTo(x + w, y + 2);
  c.lineTo(x + w + lift * 0.8, y + 2 + lift * 0.6);
  c.lineTo(x + w + lift * 0.8, y + d + lift * 0.6);
  c.lineTo(x + lift * 0.8, y + d + lift * 0.6);
  c.lineTo(x, y + d);
  c.closePath();
  c.fill();
  // Front facade with window rows.
  const face = d - lift * 0.55;
  c.fillStyle = shade(roof, -0.32);
  c.fillRect(x, y + face, w, d - face);
  c.fillStyle = h(3) < 0.5 ? "rgba(255,238,190,0.75)" : "rgba(196,226,246,0.7)";
  for (let wy = y + face + 3; wy < y + d - 3; wy += 6)
    for (let wx = x + 3; wx < x + w - 4; wx += 7) c.fillRect(wx, wy, 4, 3);
  // Roof with parapet and rooftop props.
  c.fillStyle = roof;
  c.fillRect(x, y, w, face);
  c.strokeStyle = shade(roof, 0.28);
  c.lineWidth = 2;
  c.strokeRect(x + 3, y + 3, w - 6, face - 6);
  c.fillStyle = shade(roof, -0.12);
  c.fillRect(x, y + face - 2, w, 2);
  const kind = h(4);
  if (kind < 0.08 && w > 40 && face > 36) {
    c.beginPath();
    c.arc(x + w / 2, y + face / 2, Math.min(w, face) * 0.3, 0, TAU);
    c.strokeStyle = "#F4F1E6";
    c.lineWidth = 2.5;
    c.stroke();
    c.fillStyle = "#F4F1E6";
    c.font = `bold ${Math.round(Math.min(w, face) * 0.3)}px sans-serif`;
    c.textAlign = "center";
    c.textBaseline = "middle";
    c.fillText("H", x + w / 2, y + face / 2 + 1);
  } else if (kind < 0.2) {
    c.fillStyle = "#86B76F";
    c.fillRect(x + 6, y + 6, w - 12, face - 12);
    for (let i = 0; i < 4; i++) {
      c.beginPath();
      c.arc(x + 10 + h(10 + i) * (w - 20), y + 10 + h(20 + i) * (face - 20), 4 + h(30 + i) * 3, 0, TAU);
      c.fillStyle = "#5E9453";
      c.fill();
    }
  } else
    for (let i = 0; i < 2 + h(5) * 3; i++) {
      const bx = x + 6 + h(40 + i) * (w - 18),
        by = y + 6 + h(50 + i) * (face - 16);
      c.fillStyle = "rgba(30,34,48,0.35)";
      c.fillRect(bx + 2, by + 2, 10, 8);
      c.fillStyle = i % 2 ? "#D8DCDF" : "#B9C4CC";
      c.fillRect(bx, by, 10, 8);
    }
}
function city(c: Ctx, k: number, width: number) {
  const g0 = k * TILE;
  c.fillStyle = "#666F80";
  c.fillRect(0, 0, width, TILE);
  // Blocks between avenues (every 360 in x) and streets (every 300 in G).
  const r0 = Math.floor((g0 - 40) / STREET),
    r1 = Math.floor((g0 + TILE + 40) / STREET);
  for (let row = r0; row <= r1; row++)
    for (let col = 0; col * AVENUE < width; col++) {
      const bx = col * AVENUE + 30,
        by = row * STREET + 22 - g0,
        bw = AVENUE - 60,
        bd = STREET - 44,
        h = (n: number) => hash(51, col, row, n);
      c.fillStyle = "#C3C6C9";
      c.fillRect(bx - 6, by - 6, bw + 12, bd + 12);
      const kind = h(1);
      if (kind < 0.1) {
        // Park with paths, pond and trees.
        c.fillStyle = "#8DBC7C";
        c.fillRect(bx, by, bw, bd);
        c.strokeStyle = "#E7DDBF";
        c.lineWidth = 7;
        c.beginPath();
        c.moveTo(bx, by + bd * h(2));
        c.bezierCurveTo(bx + bw * 0.3, by + bd * 0.1, bx + bw * 0.6, by + bd * 0.9, bx + bw, by + bd * h(3));
        c.stroke();
        if (h(4) < 0.6) {
          oval(c, bx + bw * 0.65, by + bd * 0.4, 42, 26, "#6FAFC0", "#D9E9DA", 4);
          oval(c, bx + bw * 0.6, by + bd * 0.35, 14, 5, "rgba(255,255,255,0.5)");
        }
        for (let i = 0; i < 12; i++)
          treeCrown(c, bx + 14 + h(10 + i) * (bw - 28), by + 14 + h(30 + i) * (bd - 28), 11 + h(50 + i) * 9, i % 3 ? "#5F9B55" : "#77AE5E", h(70 + i), 0.25);
      } else if (kind < 0.16) {
        // Plaza with paving and a fountain.
        c.fillStyle = "#DAD3C4";
        c.fillRect(bx, by, bw, bd);
        c.strokeStyle = "rgba(120,110,95,0.18)";
        c.lineWidth = 1;
        c.beginPath();
        for (let px = bx; px < bx + bw; px += 16) {
          c.moveTo(px, by);
          c.lineTo(px, by + bd);
        }
        for (let py = by; py < by + bd; py += 16) {
          c.moveTo(bx, py);
          c.lineTo(bx + bw, py);
        }
        c.stroke();
        const fx = bx + bw / 2,
          fy = by + bd / 2;
        oval(c, fx + 5, fy + 6, 44, 44, "rgba(40,40,60,0.2)");
        oval(c, fx, fy, 44, 44, "#EDE7D8", "#B8AE98", 3);
        oval(c, fx, fy, 34, 34, "#7FC0D2");
        oval(c, fx, fy, 10, 10, "#F4F1E6");
        for (let i = 0; i < 6; i++)
          treeCrown(c, bx + 18 + (i % 3) * (bw - 36) / 2, by + (i < 3 ? 18 : bd - 18), 12, "#6AA35A", i, 0.25);
      } else {
        // A grid of 2-3 × 1-2 lots per block, drawn back to front.
        const cols = 2 + Math.floor(h(5) * 2),
          rows = h(6) < 0.55 ? 2 : 1,
          lw = bw / cols,
          ld = bd / rows;
        for (let j = 0; j < rows; j++)
          for (let i = 0; i < cols; i++) {
            const seed = hash(55, col, row, i + j * 4),
              inset = 5 + hash(seed, 1) * 10,
              depth = ld * (0.7 + hash(seed, 2) * 0.3);
            building(c, bx + i * lw + inset / 2, by + j * ld + (ld - depth) * hash(seed, 3), lw - inset, depth - 5, seed);
          }
      }
    }
  // Street furniture: lane marks, crosswalks and cars.
  c.strokeStyle = "rgba(244,236,200,0.8)";
  c.lineWidth = 2.5;
  c.setLineDash([14, 14]);
  c.beginPath();
  for (let col = 0; col * AVENUE <= width; col++) {
    c.moveTo(col * AVENUE, 0);
    c.lineTo(col * AVENUE, TILE);
  }
  for (let row = r0; row <= r1; row++) {
    const y = row * STREET - g0;
    c.moveTo(0, y);
    c.lineTo(width, y);
  }
  c.stroke();
  c.setLineDash([]);
  c.fillStyle = "rgba(245,245,240,0.85)";
  for (let row = r0; row <= r1; row++)
    for (let col = 0; col * AVENUE <= width; col++) {
      const x = col * AVENUE,
        y = row * STREET - g0;
      for (let i = -2; i <= 2; i++) {
        c.fillRect(x + i * 7 - 2, y - 30, 4, 10);
        c.fillRect(x + i * 7 - 2, y + 20, 4, 10);
        c.fillRect(x - 30, y + i * 7 - 2, 10, 4);
        c.fillRect(x + 20, y + i * 7 - 2, 10, 4);
      }
    }
  scatter(k, width, 90, 30, 61, (x, y, h) => {
    if (h(3) > 0.45) return;
    const vertical = h(4) < 0.5,
      lane = h(5) < 0.5 ? -9 : 9,
      color = ["#E25D52", "#F2C94C", "#4E8FD3", "#F4F4F0", "#3F4A5A", "#62B38A"][Math.floor(h(6) * 6)];
    const cx = vertical ? Math.round(x / AVENUE) * AVENUE + lane : x,
      cy = vertical ? y : Math.round((y + g0) / STREET) * STREET - g0 + lane;
    c.save();
    c.translate(cx, cy);
    if (!vertical) c.rotate(Math.PI / 2);
    c.fillStyle = "rgba(20,20,30,0.35)";
    c.fillRect(-4, -7, 10, 17);
    c.fillStyle = color;
    c.fillRect(-5, -9, 10, 18);
    c.fillStyle = "rgba(40,55,75,0.8)";
    c.fillRect(-4, lane > 0 ? 2 : -6, 8, 4);
    c.restore();
  });
  haze(c, width, "rgba(226,232,244,0.2)");
}
/* -------------------------------------------------------- 2 星河画卷 */

const NEBULA = ["#5B3FA8", "#2A6FA8", "#A33F8E", "#2F8F9A", "#4452B8"];
function nebula(c: Ctx, k: number, width: number) {
  c.fillStyle = "#0E1231";
  c.fillRect(0, 0, width, TILE);
  scatter(k, width, 260, 320, 71, (x, y, h) => {
    const r = 140 + h(3) * 200,
      color = NEBULA[Math.floor(h(4) * NEBULA.length)];
    c.fillStyle = radial(c, x, y, 0, r, [
      [0, rgba(color, 0.32)],
      [0.5, rgba(color, 0.12)],
      [1, rgba(color, 0)],
    ]);
    c.fillRect(x - r, y - r, r * 2, r * 2);
  });
  // The "star river": a luminous band that meanders through the sky.
  const g0 = k * TILE;
  for (let y = -80; y < TILE + 80; y += 26) {
    const G = g0 + y,
      x = meander(G, 2.2, 260) * 0.65,
      w = 90 + 40 * Math.sin(G * 0.004);
    c.fillStyle = radial(c, x, y, 0, w, [
      [0, "rgba(206,196,255,0.09)"],
      [1, "rgba(206,196,255,0)"],
    ]);
    c.fillRect(x - w, y - w, w * 2, w * 2);
  }
  // Stars are baked into the nebula plane to save a full-screen layer.
  scatter(k, width, 46, 10, 73, (x, y, h) => {
    const r = 0.5 + h(3) * h(3) * 1.6,
      color = h(4) < 0.2 ? "#FFE7B8" : h(4) < 0.45 ? "#BFD6FF" : "#FFFFFF";
    c.globalAlpha = 0.4 + h(5) * 0.6;
    c.fillStyle = color;
    c.fillRect(x - r, y - r, r * 2, r * 2);
    if (h(6) < 0.05) {
      c.globalAlpha = 0.9;
      c.fillStyle = radial(c, x, y, 0, 10, [
        [0, rgba(color, 0.7)],
        [1, rgba(color, 0)],
      ]);
      c.fillRect(x - 10, y - 10, 20, 20);
      c.fillStyle = color;
      c.fillRect(x - 9, y - 0.5, 18, 1);
      c.fillRect(x - 0.5, y - 9, 1, 18);
    }
  });
  c.globalAlpha = 1;
}
function asteroid(c: Ctx, x: number, y: number, r: number, seed: number) {
  blob(c, x, y, r, seed, 5, 0.17);
  c.fillStyle = linear(c, x - r, y - r, x + r, y + r, [
    [0, "#8A7F96"],
    [0.55, "#5A5169"],
    [1, "#332D42"],
  ]);
  c.fill();
  c.strokeStyle = "#1E1A2B";
  c.lineWidth = 2;
  c.stroke();
  for (let i = 0; i < 3 + Math.floor(hash(seed, 9) * 4); i++) {
    const a = hash(seed, i) * TAU,
      d = hash(seed, i, 2) * r * 0.6,
      cr = r * (0.1 + hash(seed, i, 3) * 0.16),
      cx = x + Math.cos(a) * d,
      cy = y + Math.sin(a) * d;
    oval(c, cx, cy, cr, cr * 0.85, "#3E3650");
    c.beginPath();
    c.arc(cx, cy, cr, 0.2, Math.PI * 0.9);
    c.strokeStyle = "rgba(190,178,210,0.45)";
    c.lineWidth = 1.5;
    c.stroke();
  }
  blob(c, x - r * 0.25, y - r * 0.28, r * 0.45, seed + 0.4, 4, 0.2);
  c.fillStyle = "rgba(220,210,240,0.16)";
  c.fill();
}
function station(c: Ctx, x: number, y: number, seed: number) {
  const s = 0.8 + hash(seed, 1) * 0.5;
  c.save();
  c.translate(x, y);
  c.scale(s, s);
  c.rotate(Math.floor(hash(seed, 2) * 4) * (Math.PI / 2));
  // Solar arrays on a truss.
  c.fillStyle = "#6D7890";
  c.fillRect(-150, -3, 300, 6);
  for (const side of [-1, 1])
    for (let i = 0; i < 2; i++) {
      const px = side * (60 + i * 46);
      c.fillStyle = "#223C74";
      c.fillRect(px - 20, -34, 40, 68);
      c.strokeStyle = "#5C86C8";
      c.lineWidth = 1;
      c.beginPath();
      for (let gx = -20; gx <= 20; gx += 10) {
        c.moveTo(px + gx, -34);
        c.lineTo(px + gx, 34);
      }
      for (let gy = -34; gy <= 34; gy += 11) {
        c.moveTo(px - 20, gy);
        c.lineTo(px + 20, gy);
      }
      c.stroke();
      c.strokeStyle = "#141C33";
      c.lineWidth = 2;
      c.strokeRect(px - 20, -34, 40, 68);
    }
  // Hull modules.
  c.fillStyle = linear(c, -40, -40, 40, 40, [
    [0, "#C5CEDD"],
    [1, "#7A869E"],
  ]);
  c.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU + Math.PI / 8;
    c.lineTo(Math.cos(a) * 38, Math.sin(a) * 38);
  }
  c.closePath();
  c.fill();
  c.strokeStyle = "#1E2436";
  c.lineWidth = 2.5;
  c.stroke();
  oval(c, 0, 0, 18, 18, "#56617A", "#1E2436", 2);
  oval(c, 0, 0, 9, 9, "#8FE3FF");
  c.strokeStyle = "rgba(40,50,70,0.4)";
  c.lineWidth = 1;
  c.beginPath();
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * TAU;
    c.moveTo(Math.cos(a) * 20, Math.sin(a) * 20);
    c.lineTo(Math.cos(a) * 36, Math.sin(a) * 36);
  }
  c.stroke();
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * TAU;
    oval(c, Math.cos(a) * 30, Math.sin(a) * 30, 2.4, 2.4, i % 2 ? "#FF6B7A" : "#9FF0FF");
  }
  c.restore();
}
/* -------------------------------------------------------- 3 翡翠雨林 */

const CANOPY = ["#4E8A4F", "#5E9A55", "#3F7C4B", "#6FA35A", "#357047", "#7FAE5C", "#4A8358"];
function rainforest(c: Ctx, k: number, width: number) {
  const g0 = k * TILE,
    river = (G: number) => meander(G, 5.3, 760),
    half = (G: number) => 40 + 14 * Math.sin(G * 0.0025);
  c.fillStyle = "#2F5A3A";
  c.fillRect(0, 0, width, TILE);
  ribbon(c, k, river, (G) => half(G) + 22, "#6E6A45");
  ribbon(c, k, river, (G) => half(G) + 10, "#8C8458");
  ribbon(c, k, river, half, linear(c, 0, 0, width, 0, [[0, "#3F8C85"], [1, "#4E9C8C"]]));
  riverLines(c, k, river, half, "rgba(220,255,240,0.45)");
  // Stone ruins in clearings.
  const clearings: [number, number, number][] = [];
  scatter(k, width, 620, 200, 91, (x, y, h) => {
    if (h(3) > 0.34 || Math.abs(x - river(y + g0)) < 190) return;
    clearings.push([x, y, 88]);
    blob(c, x, y, 96, h(4), 6, 0.14);
    c.fillStyle = "#6F8455";
    c.fill();
    for (let step = 0; step < 3; step++) {
      const s = 62 - step * 18;
      c.fillStyle = "rgba(20,30,20,0.35)";
      c.fillRect(x - s + 6, y - s + 8, s * 2, s * 2);
      c.fillStyle = step % 2 ? "#A9A68F" : "#9B9982";
      c.fillRect(x - s, y - s, s * 2, s * 2);
      c.fillStyle = "#7E7C68";
      c.fillRect(x - s, y + s - 5, s * 2, 5);
    }
    oval(c, x, y, 9, 9, "#5A5847");
    for (let i = 0; i < 7; i++) oval(c, x + (h(10 + i) - 0.5) * 110, y + (h(20 + i) - 0.5) * 110, 7 + h(30 + i) * 8, 5, "rgba(95,140,70,0.75)");
  });
  // Canopy crowns, sorted top to bottom for consistent overlap across seams.
  const crowns: [number, number, number, number][] = [];
  scatter(k, width, 58, 90, 93, (x, y, h) => {
    const G = y + g0;
    if (Math.abs(x - river(G)) < half(G) + 26) return;
    if (clearings.some(([cx, cy, r]) => Math.hypot(x - cx, y - cy) < r)) return;
    crowns.push([x, y, 26 + h(3) * 30, h(4)]);
  });
  crowns.sort((a, b) => a[1] - b[1]);
  for (const [x, y, r, seed] of crowns) {
    const color = seed < 0.04 ? "#D9826A" : seed < 0.07 ? "#E3B65A" : CANOPY[Math.floor(seed * 1000) % CANOPY.length];
    treeCrown(c, x, y, r, color, seed * 10, 0.4);
  }
  haze(c, width, "rgba(200,236,214,0.12)");
}
/* -------------------------------------------------------- 4 琥珀沙丘 */

function dunes(c: Ctx, k: number, width: number) {
  const g0 = k * TILE;
  c.fillStyle = "#E2B676";
  c.fillRect(0, 0, width, TILE);
  // Dune ridges: lit windward face above, shaded lee face below.
  const spacing = 150;
  for (let j = Math.floor((g0 - 120) / spacing); j <= Math.floor((g0 + TILE + 120) / spacing); j++) {
    const base = j * spacing - g0,
      phase = hash(101, j) * TAU,
      ridge: number[][] = [];
    for (let x = -20; x <= width + 20; x += 20)
      ridge.push([x, base + 34 * Math.sin(x * 0.005 + phase) + 16 * Math.sin(x * 0.017 + phase * 2)]);
    c.beginPath();
    ridge.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
    for (let i = ridge.length - 1; i >= 0; i--) c.lineTo(ridge[i][0], ridge[i][1] - 60);
    c.closePath();
    c.fillStyle = linear(c, 0, base - 60, 0, base, [
      [0, "rgba(255,226,170,0)"],
      [1, "rgba(255,230,178,0.55)"],
    ]);
    c.fill();
    c.beginPath();
    ridge.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y)));
    for (let i = ridge.length - 1; i >= 0; i--) c.lineTo(ridge[i][0], ridge[i][1] + 48);
    c.closePath();
    c.fillStyle = linear(c, 0, base, 0, base + 48, [
      [0, "rgba(160,96,52,0.42)"],
      [1, "rgba(160,96,52,0)"],
    ]);
    c.fill();
    c.strokeStyle = "rgba(150,95,55,0.18)";
    c.lineWidth = 1.2;
    for (let r = 1; r <= 4; r++) {
      c.beginPath();
      ridge.forEach(([x, y], i) => (i ? c.lineTo(x, y - r * 12) : c.moveTo(x, y - r * 12)));
      c.stroke();
    }
  }
  // Caravan track.
  const track = (G: number) => meander(G, 6.2, 540);
  ribbon(c, k, track, () => 16, "rgba(196,142,88,0.35)");
  for (const off of [-7, 7]) ribbon(c, k, (G) => track(G) + off, () => 1.4, "rgba(120,78,46,0.45)");
  // Mesas.
  scatter(k, width, 440, 160, 103, (x, y, h) => {
    if (h(3) > 0.3 || Math.abs(x - track(y + g0)) < 140) return;
    const r = 50 + h(4) * 50,
      seed = h(5) * 10;
    blob(c, x + 22, y + 26, r, seed, 6, 0.22, 0.72);
    c.fillStyle = "rgba(110,60,35,0.3)";
    c.fill();
    blob(c, x + 4, y + 14, r, seed, 6, 0.22, 0.72);
    c.fillStyle = "#9E6341";
    c.fill();
    blob(c, x, y, r, seed, 6, 0.22, 0.72);
    c.fillStyle = "#CB935E";
    c.fill();
    c.strokeStyle = "rgba(120,72,44,0.5)";
    c.lineWidth = 1.5;
    c.stroke();
    blob(c, x - r * 0.15, y - r * 0.12, r * 0.6, seed + 1, 5, 0.2, 0.7);
    c.fillStyle = "rgba(240,196,140,0.45)";
    c.fill();
  });
  // Ruined colonnades and an occasional oasis.
  scatter(k, width, 560, 160, 107, (x, y, h) => {
    if (Math.abs(x - track(y + g0)) < 90) return;
    if (h(3) < 0.2) {
      for (let i = 0; i < 6; i++) {
        const px = x + (i % 3) * 38 - 38,
          py = y + Math.floor(i / 3) * 70 - 35;
        if (h(10 + i) < 0.2) continue;
        oval(c, px + 8, py + 10, 12, 8, "rgba(110,64,36,0.3)");
        oval(c, px, py, 11, 11, "#E9CFA1", "#9E7449", 2);
        oval(c, px - 3, py - 3, 5, 5, "#F7E6C4");
      }
      c.fillStyle = "#D9B886";
      c.fillRect(x - 70, y - 70, 20, 140 * (0.4 + h(4) * 0.6));
      c.fillStyle = "#A9804F";
      c.fillRect(x - 70, y - 70 + 140 * (0.4 + h(4) * 0.6), 20, 6);
    } else if (h(3) < 0.3) {
      oval(c, x, y, 70, 44, "#94B96A");
      oval(c, x, y, 50, 30, "#4FA2A6", "#E7D9A8", 3);
      oval(c, x - 14, y - 8, 16, 5, "rgba(255,255,255,0.45)");
      for (let i = 0; i < 4; i++) {
        const px = x + Math.cos(i * 1.7) * 62,
          py = y + Math.sin(i * 1.7) * 38;
        oval(c, px + 8, py + 8, 14, 10, "rgba(60,50,20,0.3)");
        for (let f = 0; f < 6; f++) {
          const a = (f / 6) * TAU + i;
          c.strokeStyle = f % 2 ? "#4F8A45" : "#6BA356";
          c.lineWidth = 5;
          c.lineCap = "round";
          c.beginPath();
          c.moveTo(px, py);
          c.quadraticCurveTo(px + Math.cos(a) * 10, py + Math.sin(a) * 10 - 4, px + Math.cos(a) * 18, py + Math.sin(a) * 18);
          c.stroke();
        }
      }
    }
  });
  haze(c, width, "rgba(255,232,196,0.12)");
}
/* -------------------------------------------------------- 5 冰晶极光 */

function crystal(c: Ctx, x: number, y: number, size: number, seed: number) {
  oval(c, x + size * 0.5, y + size * 0.55, size * 1.1, size * 0.6, "rgba(40,80,120,0.25)");
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (hash(seed, i) - 0.5) * 1.8,
      len = size * (0.6 + hash(seed, i, 2) * 0.8),
      w = size * 0.2,
      bx = x + (hash(seed, i, 3) - 0.5) * size * 0.6,
      tip = [bx + Math.cos(a) * len, y + Math.sin(a) * len],
      nx = Math.cos(a + Math.PI / 2) * w,
      ny = Math.sin(a + Math.PI / 2) * w;
    c.beginPath();
    c.moveTo(bx - nx, y - ny);
    c.lineTo(tip[0], tip[1]);
    c.lineTo(bx + nx, y + ny);
    c.closePath();
    c.fillStyle = "#9FDDF0";
    c.fill();
    c.beginPath();
    c.moveTo(bx, y);
    c.lineTo(tip[0], tip[1]);
    c.lineTo(bx + nx, y + ny);
    c.closePath();
    c.fillStyle = "#5FA9CF";
    c.fill();
    c.strokeStyle = "#2F6E97";
    c.lineWidth = 1.2;
    c.beginPath();
    c.moveTo(bx - nx, y - ny);
    c.lineTo(tip[0], tip[1]);
    c.lineTo(bx + nx, y + ny);
    c.stroke();
  }
}
function ice(c: Ctx, k: number, width: number) {
  const g0 = k * TILE;
  c.fillStyle = "#CFE3EC";
  c.fillRect(0, 0, width, TILE);
  // Wind-packed ice plates with bright pressure ridges.
  scatter(k, width, 210, 140, 111, (x, y, h) => {
    const r = 70 + h(3) * 70,
      seed = h(4) * 10;
    blob(c, x, y, r, seed, 5, 0.22, 0.72);
    c.fillStyle = h(5) < 0.5 ? "#E3F1F7" : "#C3DCE8";
    c.fill();
    c.strokeStyle = "rgba(255,255,255,0.95)";
    c.lineWidth = 3;
    c.stroke();
    blob(c, x + 3, y + 4, r, seed, 5, 0.22, 0.72);
    c.strokeStyle = "rgba(96,150,186,0.35)";
    c.lineWidth = 2;
    c.stroke();
  });
  // Open-water leads with floes.
  for (const seed of [2.7, 8.1]) {
    const lead = (G: number) => meander(G, seed, 820),
      half = (G: number) => 26 + 18 * Math.sin(G * 0.0021 + seed);
    ribbon(c, k, lead, (G) => half(G) + 9, "#EAF6FB");
    ribbon(c, k, lead, (G) => half(G) + 4, "#86BCD6");
    ribbon(c, k, lead, half, linear(c, 0, 0, width, 0, [[0, "#23527A"], [1, "#2F6690"]]));
    for (let y = -40; y < TILE + 40; y += 30) {
      const G = g0 + y,
        h = hash(113, Math.floor(G / 30), seed);
      if (h < 0.5) continue;
      const fx = lead(G) + (h - 0.75) * half(G) * 2.4,
        r = 5 + h * 9;
      blob(c, fx, y, r, h * 9, 4, 0.2, 0.8);
      c.fillStyle = "#E4F3FA";
      c.fill();
    }
  }
  // Pressure cracks.
  c.lineCap = "round";
  scatter(k, width, 170, 90, 117, (x, y, h) => {
    if (h(3) > 0.5) return;
    let px = x,
      py = y;
    const pts = [[px, py]];
    for (let i = 0; i < 5; i++) {
      px += (h(10 + i) - 0.5) * 60;
      py += 10 + h(20 + i) * 22;
      pts.push([px, py]);
    }
    for (const [color, w, dx] of [
      ["rgba(255,255,255,0.8)", 2.5, -1],
      ["rgba(80,130,170,0.55)", 1.4, 0],
    ] as const) {
      c.strokeStyle = color;
      c.lineWidth = w;
      c.beginPath();
      pts.forEach(([ax, ay], i) => (i ? c.lineTo(ax + dx, ay + dx) : c.moveTo(ax + dx, ay + dx)));
      c.stroke();
    }
  });
  scatter(k, width, 150, 60, 119, (x, y, h) => {
    if (h(3) < 0.3) for (let i = 0; i < 2 + h(4) * 4; i++) pine(c, x + (hash(i, x) - 0.5) * 70, y + (hash(y, i) - 0.5) * 50, 12 + hash(i, y) * 8, "#2E5B5A", "rgba(240,250,255,0.9)");
  });
  scatter(k, width, 480, 120, 123, (x, y, h) => {
    if (h(3) < 0.22) crystal(c, x, y, 26 + h(4) * 20, h(5) * 100);
  });
  haze(c, width, "rgba(214,236,252,0.16)");
}
/* ------------------------------------------------------------ decor */

/**
 * Sparse overlay objects (clouds, mist, debris, snow) are drawn as cached
 * sprite variants at hashed positions instead of full-screen tiles, so the
 * per-frame fill cost only covers the objects themselves.
 */
interface Decor {
  id: string;
  speed: number;
  parallax: number;
  alpha: number;
  add?: boolean;
  depth: number;
  cell: number;
  chance: number;
  variants: number;
  w: number;
  h: number;
  res: number;
  paint(c: Ctx, variant: number): void;
}
const DECOR = {
  cloud: {
    cell: 340,
    chance: 0.3,
    variants: 6,
    w: 420,
    h: 250,
    res: 0.5,
    paint: (c: Ctx, v: number) => cloudBank(c, 0, 0, 62 + (v % 3) * 20, v * 977 + 1, 0.9),
  },
  wisp: {
    cell: 420,
    chance: 0.35,
    variants: 4,
    w: 330,
    h: 70,
    res: 0.5,
    paint(c: Ctx, v: number) {
      for (let i = 0; i < 4; i++) oval(c, -110 + i * 38, Math.sin(i + v * 1.7) * 10, 70 - i * 8, 14, "rgba(255,255,255,0.55)", null, 0, -0.08);
    },
  },
  smog: {
    cell: 380,
    chance: 0.3,
    variants: 4,
    w: 380,
    h: 120,
    res: 0.5,
    paint(c: Ctx, v: number) {
      for (let i = 0; i < 5; i++) oval(c, (hash(v, i, 1) - 0.5) * 160, (hash(v, i, 2) - 0.5) * 40, 60 + hash(v, i, 3) * 40, 22, "rgba(255,250,240,0.3)");
    },
  },
  rock: {
    cell: 250,
    chance: 0.6,
    variants: 12,
    w: 200,
    h: 200,
    res: 1,
    paint(c: Ctx, v: number) {
      if (v < 8) asteroid(c, 0, 0, 18 + (v / 8) * (v / 8) * 70, v * 13 + 5);
      else for (let i = 0; i < 5; i++) asteroid(c, (hash(v, i, 1) - 0.5) * 90, (hash(v, i, 2) - 0.5) * 70, 5 + hash(v, i, 3) * 9, v * 7 + i);
    },
  },
  station: { cell: 640, chance: 0.32, variants: 3, w: 420, h: 420, res: 1, paint: (c: Ctx, v: number) => station(c, 0, 0, v * 131 + 7) },
  dust: {
    cell: 120,
    chance: 0.35,
    variants: 3,
    w: 6,
    h: 48,
    res: 1,
    paint(c: Ctx, v: number) {
      c.fillStyle = v % 2 ? "rgba(190,210,255,0.55)" : "rgba(255,210,240,0.45)";
      c.fillRect(-0.75, -12 - v * 5, 1.5, 24 + v * 10);
    },
  },
  mist: {
    cell: 300,
    chance: 0.4,
    variants: 3,
    w: 560,
    h: 220,
    res: 0.4,
    paint(c: Ctx, v: number) {
      const r = 110 + v * 25;
      c.save();
      c.scale(1.6, 0.6);
      c.fillStyle = radial(c, 0, 0, 0, r, [
        [0, "rgba(236,255,246,0.45)"],
        [1, "rgba(236,255,246,0)"],
      ]);
      c.fillRect(-r, -r, r * 2, r * 2);
      c.restore();
    },
  },
  sand: {
    cell: 260,
    chance: 0.35,
    variants: 3,
    w: 300,
    h: 70,
    res: 0.5,
    paint(c: Ctx, v: number) {
      c.strokeStyle = "rgba(255,238,205,0.4)";
      c.lineWidth = 6 + v * 5;
      c.lineCap = "round";
      c.beginPath();
      c.moveTo(-130, 0);
      c.bezierCurveTo(-50, -18, 30, 18, 130, -6);
      c.stroke();
    },
  },
  aurora: {
    cell: 460,
    chance: 0.45,
    variants: 3,
    w: 240,
    h: 620,
    res: 0.4,
    paint(c: Ctx, v: number) {
      const color = v % 2 ? "#A77BFF" : "#5CF2B2";
      for (let y = -300; y < 300; y += 6) {
        const x = Math.sin(y * 0.012 + v) * 50,
          w = 45 + 20 * Math.sin(y * 0.02 + v * 2),
          a = (0.1 + 0.06 * Math.sin(y * 0.05 + v)) * Math.min(1, (300 - Math.abs(y)) / 120);
        c.fillStyle = linear(c, x - w, 0, x + w, 0, [
          [0, rgba(color, 0)],
          [0.5, rgba(color, a)],
          [1, rgba(color, 0)],
        ]);
        c.fillRect(x - w, y, w * 2, 6);
      }
    },
  },
  snow: {
    cell: 70,
    chance: 0.6,
    variants: 4,
    w: 8,
    h: 8,
    res: 1,
    paint(c: Ctx, v: number) {
      c.fillStyle = `rgba(255,255,255,${0.45 + v * 0.12})`;
      c.beginPath();
      c.arc(0, 0, 1 + v * 0.7, 0, TAU);
      c.fill();
    },
  },
};
const decor = (kind: keyof typeof DECOR, speed: number, parallax: number, alpha: number, depth: number, add = false): Decor => ({
  id: kind,
  speed,
  parallax,
  alpha,
  depth,
  add,
  ...DECOR[kind],
});

function paintDecor(
  c: Ctx,
  biome: number,
  d: Decor,
  tick: number,
  camera: number,
  player: { x: number; y: number },
  top: number,
  bottom: number,
) {
  const offset = d.depth ? sceneryOffset(tick, d.depth, player.x - camera, player.y) : { x: 0, y: 0 },
    sx = camera * d.parallax - offset.x,
    scroll = tick * d.speed + (d.depth ? (1000 - player.y) * d.depth * 0.025 : 0),
    salt = biome * 100 + d.id.length * 7 + d.cell;
  c.save();
  c.globalAlpha = d.alpha;
  if (d.add) c.globalCompositeOperation = "lighter";
  for (let cy = Math.floor((top - scroll - d.h) / d.cell); cy <= Math.floor((bottom - scroll + d.h) / d.cell); cy++)
    for (let cx = Math.floor((sx - d.w) / d.cell); cx <= Math.floor((sx + 720 + d.w) / d.cell); cx++) {
      if (hash(salt, cx, cy, 0) >= d.chance) continue;
      const x = cx * d.cell + hash(salt, cx, cy, 1) * d.cell - sx,
        y = cy * d.cell + hash(salt, cx, cy, 2) * d.cell + scroll;
      if (x < -d.w / 2 || x > 720 + d.w / 2 || y < top - d.h / 2 || y > bottom + d.h / 2) continue;
      const v = Math.floor(hash(salt, cx, cy, 3) * d.variants),
        canvas = raster(`decor:${biome}:${d.id}:${v}`, d.w, d.h, d.res, (g) => {
          g.translate(d.w / 2, d.h / 2);
          d.paint(g, v);
        });
      if (canvas) c.drawImage(canvas, x - d.w / 2, y - d.h / 2, d.w, d.h);
      else {
        c.save();
        c.translate(x, y);
        d.paint(c, v);
        c.restore();
      }
    }
  c.restore();
}

/* ------------------------------------------------------------ biomes */

const ground = (paint: Layer["paint"], id: string): Layer => ({
  id,
  speed: GROUND_SPEED,
  parallax: 1,
  res: 1,
  alpha: 1,
  depth: 0,
  paint,
});
const BIOMES: { base: string; tint: string; layers: (Layer | Decor)[] }[] = [
  {
    base: "#A7C487",
    tint: "#FFE7B5",
    layers: [ground(farmland, "farm"), decor("cloud", 1.05, 1.1, 0.8, 1), decor("wisp", 1.8, 1.35, 0.7, 3)],
  },
  { base: "#666F80", tint: "#FFD9B0", layers: [ground(city, "city"), decor("smog", 1.5, 1.25, 0.8, 2)] },
  {
    base: "#0E1231",
    tint: "#9D8BFF",
    layers: [
      { id: "nebula", speed: 0.08, parallax: 0.2, res: 0.5, alpha: 1, depth: 0, paint: nebula },
      decor("rock", GROUND_SPEED, 1, 1, 0),
      decor("station", GROUND_SPEED, 1, 1, 0),
      decor("dust", 2.1, 1.4, 0.8, 3),
    ],
  },
  { base: "#2F5A3A", tint: "#D8FFB8", layers: [ground(rainforest, "jungle"), decor("mist", 1.25, 1.15, 0.9, 1)] },
  { base: "#E2B676", tint: "#FFC98A", layers: [ground(dunes, "dune"), decor("sand", 1.5, 1.25, 0.9, 2)] },
  {
    base: "#CFE3EC",
    tint: "#9FFFE0",
    layers: [ground(ice, "ice"), decor("aurora", 0.9, 1.1, 1, 1, true), decor("snow", 2.3, 1.4, 0.9, 3)],
  },
];
export const BIOME_BASE = BIOMES.map((b) => b.base);

let activeBiome = -1;
/** Upcoming tiles rasterised ahead of time, at most one per frame. */
let prewarmBudget = 0;
const live = new Map<string, Set<number>>();
function paintLayer(
  c: Ctx,
  biome: number,
  layer: Layer,
  tick: number,
  camera: number,
  player: { x: number; y: number },
  top: number,
  bottom: number,
) {
  const width = Math.ceil(720 + 720 * layer.parallax),
    offset = layer.depth ? sceneryOffset(tick, layer.depth, player.x - camera, player.y) : { x: 0, y: 0 },
    sx = Math.max(0, Math.min(width - 720, camera * layer.parallax - offset.x)),
    scroll = tick * layer.speed + (layer.depth ? (1000 - player.y) * layer.depth * 0.025 : 0),
    first = Math.floor((top - scroll) / TILE),
    last = Math.floor((bottom - scroll) / TILE),
    prefix = `scene:${biome}:${layer.id}:`,
    tile = (k: number) => raster(prefix + k, width, TILE, layer.res, (g) => layer.paint(g, k, width));
  c.save();
  c.globalAlpha = layer.alpha;
  if (layer.add) c.globalCompositeOperation = "lighter";
  for (let k = first; k <= last; k++) {
    const y = k * TILE + scroll,
      canvas = tile(k);
    // One logical unit of overlap hides resampling seams between tiles.
    if (canvas) c.drawImage(canvas, sx * layer.res, 0, 720 * layer.res, TILE * layer.res, 0, y, 720, TILE + 1.5);
    else {
      c.save();
      c.beginPath();
      c.rect(0, y, 720, TILE);
      c.clip();
      c.translate(-sx, y);
      layer.paint(c, k, width);
      c.restore();
    }
  }
  c.restore();
  // Tiles scroll downward (k decreasing enters from the top): drop the ones
  // that left the bottom and warm the next one before it is needed.
  const kept = live.get(prefix) ?? new Set<number>();
  live.set(prefix, kept);
  for (let k = first; k <= last + 1; k++) kept.add(k);
  for (const k of kept)
    if (k > last + 1 || k < first - 2) {
      dropKey(prefix + k);
      kept.delete(k);
    }
  if (prewarmBudget > 0 && !isCached(prefix + (first - 1))) {
    prewarmBudget--;
    if (tile(first - 1)) kept.add(first - 1);
  }
}

/**
 * Paints the biome in logical 720-wide screen space. The ground plane is
 * glued to world coordinates; overlays add camera and player parallax.
 * `top`/`bottom` extend painting into letterbox bands.
 */
export function paintScenery(
  c: Ctx,
  scene: number,
  tick: number,
  player: { x: number; y: number },
  camera = 0,
  top = 0,
  bottom = 1280,
) {
  const biome = ((scene % 6) + 6) % 6,
    theme = BIOMES[biome];
  if (biome !== activeBiome) {
    if (activeBiome >= 0) dropCached(`scene:${activeBiome}:`);
    live.clear();
    activeBiome = biome;
  }
  prewarmBudget = 1;
  c.save();
  for (const layer of theme.layers)
    if ("cell" in layer) paintDecor(c, biome, layer, tick, camera, player, top, bottom);
    else paintLayer(c, biome, layer, tick, camera, player, top, bottom);
  // Atmosphere: warm key light across the top and darkened side edges,
  // painted as narrow bands to keep the full-screen fill cost low.
  const light = raster(`scene-light:${biome}`, 720, 420, 0.25, (g) => {
    g.fillStyle = radial(g, 520, -80, 0, 620, [
      [0, rgba(theme.tint, 0.32)],
      [0.55, rgba(theme.tint, 0.08)],
      [1, rgba(theme.tint, 0)],
    ]);
    g.fillRect(0, 0, 720, 420);
  });
  if (light) c.drawImage(light, 0, 0, 720, 420);
  const edge = raster("scene-edge", 80, 16, 0.5, (g) => {
    g.fillStyle = linear(g, 0, 0, 80, 0, [
      [0, "rgba(10,14,30,0.34)"],
      [1, "rgba(10,14,30,0)"],
    ]);
    g.fillRect(0, 0, 80, 16);
  });
  if (edge) {
    c.drawImage(edge, 0, top, 80, bottom - top);
    c.save();
    c.translate(720, 0);
    c.scale(-1, 1);
    c.drawImage(edge, 0, top, 80, bottom - top);
    c.restore();
  }
  c.restore();
}
